import { supabase } from './supabaseClient';
import { getLang, t } from './i18n';

// ============================================================
// Pornirea conectarii la fiecare retea, intr-un singur loc (16 septembrie 2026,
// PAGINA_CLIENT_PLAN.md, Etapa 4, pasul 6).
//
// Pana acum adresele de autorizare traiau inauntrul butoanelor din `App.js`.
// Randul de conturi din pagina clientului are nevoie de aceleasi adrese, iar un
// sir de permisiuni copiat in doua locuri se desincronizeaza fara ca nimeni sa
// observe: conectarea aduce mai putin decat trebuie si defectul apare abia peste
// saptamani, cand o publicare pica. Deci se scrie o singura data, aici.
//
// Codul e mutat ca atare din `App.js`, cu tot cu explicatiile lui.
// ============================================================

export const CLIENT_KEY_TIKTOK = process.env.REACT_APP_TIKTOK_CLIENT_KEY;
export const CLIENT_ID_LINKEDIN = process.env.REACT_APP_LINKEDIN_CLIENT_ID;
// Threads are app ID PROPRIU, altul decat cel de Facebook: cazul de utilizare
// „Access the Threads API" produce o a doua pereche id+secret in aceeasi
// aplicatie Meta. Documentatia lor o spune raspicat — vezi THREADS_PLAN.md §1.
export const CLIENT_ID_THREADS = process.env.REACT_APP_THREADS_APP_ID;
// X (10 octombrie 2026, X_PLAN.md). Client ID-ul aplicatiei „NM Social by New Mind" nu e
// secret: apare in adresa de autorizare. Secretul sta doar in Supabase.
export const CLIENT_ID_X = process.env.REACT_APP_X_CLIENT_ID;

// Codul de unica folosinta al conectarii (9 octombrie 2026, AUDIT_SECURITATE_PLAN.md, etapa 3).
// Pana acum `state` era doar id-ul clientului, deci pagina de intoarcere primea orice cod, si
// unul pe care nu-l ceruse nimeni din fila aceasta: un link pregatit de altcineva, deschis de un
// om logat, i-ar fi legat clientului contul ALTCUIVA. Acum plecarea isi noteaza un cod la
// intamplare, iar intoarcerea il compara si il arde.
//
// `localStorage`, nu `sessionStorage`: pe telefon TikTok si Threads pot aduce omul inapoi in
// alta fila a aceluiasi browser, unde `sessionStorage` e gol. Sesiunea de autentificare sta tot
// in `localStorage`, deci codul ajunge exact acolo unde ajunge si ea.
const CHEIE_STARE = 'nm_oauth_stare';
const STARE_VALABILA_MS = 30 * 60 * 1000;

// `verificator` e doar pentru X: codul PKCE, care trebuie sa ajunga de la plecare la intoarcere.
function stareNoua(clientId, verificator = null) {
  const octeti = new Uint8Array(16);
  window.crypto.getRandomValues(octeti);
  const stare = `${clientId}.${Array.from(octeti, b => b.toString(16).padStart(2, '0')).join('')}`;
  try { localStorage.setItem(CHEIE_STARE, JSON.stringify({ stare, la: Date.now(), verificator })); } catch (e) { /* intoarcerea va refuza */ }
  return stare;
}

// Verificatorul PKCE al plecarii pe care tocmai a recunoscut-o `clientDinStare`. Sta doar in
// memoria paginii: nota din `localStorage` e arsa la prima citire.
let verificatorulPlecarii = null;
export function verificatorDinStare() { return verificatorulPlecarii; }

// Intoarce id-ul clientului daca `state` din adresa e chiar cel notat la plecare; altfel null.
// Functiile de pe server primesc in continuare doar id-ul clientului, ca pana acum.
export function clientDinStare(stareIntoarsa) {
  let notat = null;
  try {
    notat = JSON.parse(localStorage.getItem(CHEIE_STARE) || 'null');
    localStorage.removeItem(CHEIE_STARE);
  } catch (e) { return null; }
  if (!notat || !notat.stare || notat.stare !== stareIntoarsa) return null;
  if (Date.now() - Number(notat.la || 0) > STARE_VALABILA_MS) return null;
  verificatorulPlecarii = notat.verificator || null;
  const clientId = String(stareIntoarsa).split('.')[0];
  return /^[0-9]+$/.test(clientId) ? clientId : null;
}

export function conecteazaFacebook(clientId) {
  const appId = '1848347705828153';
  const redirectUri = encodeURIComponent('https://social.newmind.ro/auth/facebook/callback');
  // Scope: publicare (pages_manage_posts) + citire pagină (pages_read_engagement) + listă pagini (pages_show_list) + citire comentarii utilizatori pt Inbox (pages_read_user_content) + răspuns la comentarii (pages_manage_engagement) + DM-uri (pages_messaging) + abonare webhooks per-pagină / subscribed_apps (pages_manage_metadata).
  // read_insights NU e desfiintata: cere App Review si depinde de
  // pages_read_engagement + pages_show_list, ambele aprobate.
  // business_management e obligatorie, nu optionala: paginile clientilor sunt
  // detinute de portofolii de business, iar fara ea Facebook acorda
  // pages_show_list dar intoarce /me/accounts GOL, fara nicio eroare.
  // instagram_manage_comments nu e optionala pentru Inbox: fara ea, API-ul
  // intoarce textul comentariului dar NU si username-ul autorului.
  // instagram_manage_insights e ceruta din timp, ca sa nu fie nevoie de inca o
  // reconectare la Faza 3.
  const scope = encodeURIComponent('pages_manage_posts,pages_read_engagement,read_insights,pages_show_list,pages_read_user_content,pages_manage_engagement,pages_messaging,pages_manage_metadata,business_management,instagram_basic,instagram_content_publish,instagram_manage_comments,instagram_manage_insights,instagram_manage_messages');
  const state = stareNoua(clientId);
  window.location.href = `https://www.facebook.com/v21.0/dialog/oauth?client_id=${appId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}&response_type=code&auth_type=rerequest`;
}

export function conecteazaTikTok(clientId) {
  // redirect_uri dinamic: merge automat si pe preview Vercel, si pe productie.
  const redirectUri = encodeURIComponent(window.location.origin + '/auth/tiktok/callback');
  // Doar ce foloseste efectiv aplicatia. `video.upload` era cerut si nefolosit,
  // iar TikTok intarzie recenzia pentru scopuri pe care nu le poti demonstra.
  const scope = encodeURIComponent('user.info.basic,video.publish');
  const state = stareNoua(clientId);
  // Limba ecranului de autorizare. Fara `lang`, TikTok si-o alege singur dupa regiunea
  // contului, NU dupa limba aplicatiei: pe 20 septembrie 2026 ecranul venea in romana desi
  // aplicatia era pe engleza si sesiunea tiktok.com tot pe engleza. Parametrul nu e documentat
  // pentru /v2/auth/authorize/, dar e ascultat — probat pe viu in aceeasi zi, pe contul Gagai.
  // Conteaza pentru recenzorii TikTok, care citesc in engleza si vad ecranul asta in film.
  const limba = getLang() === 'en' ? 'en' : 'ro-RO';
  window.location.href = `https://www.tiktok.com/v2/auth/authorize/?client_key=${CLIENT_KEY_TIKTOK}&scope=${scope}&response_type=code&redirect_uri=${redirectUri}&state=${state}&lang=${limba}`;
}

export function conecteazaThreads(clientId) {
  // Login-ul NU trece prin Facebook: Threads are domeniul lui de autorizare.
  const redirectUri = encodeURIComponent(window.location.origin + '/auth/threads/callback');
  // `threads_basic` e obligatorie si nu se poate scoate; `threads_content_publish`
  // e singura in plus de care avem nevoie acum. Comentariile si cifrele raman
  // pentru o runda ulterioara (decizia Luminitei, 19 septembrie 2026).
  const scope = encodeURIComponent('threads_basic,threads_content_publish');
  const state = stareNoua(clientId);
  window.location.href = `https://threads.com/oauth/authorize?client_id=${CLIENT_ID_THREADS}&redirect_uri=${redirectUri}&scope=${scope}&response_type=code&state=${state}`;
}

export function conecteazaLinkedIn(clientId) {
  // redirect_uri dinamic, ca la TikTok. Amandoua adresele (productie si localhost)
  // sunt trecute in portalul LinkedIn; orice alta origine e refuzata de ei.
  const redirectUri = encodeURIComponent(window.location.origin + '/auth/linkedin/callback');
  // w_member_social da dreptul de a publica, openid si profile spun in numele cui.
  const scope = encodeURIComponent('openid profile email w_member_social');
  const state = stareNoua(clientId);
  window.location.href = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${CLIENT_ID_LINKEDIN}&redirect_uri=${redirectUri}&state=${state}&scope=${scope}`;
}

// X: OAuth 2.0 cu PKCE. Browserul face un cod la intamplare (verificatorul), trimite la X doar
// amprenta lui SHA-256, iar la intoarcere il da functiei `x-oauth`, care il arata lui X ca dovada
// ca aceeasi fereastra a pornit si a incheiat conectarea.
function base64Url(octeti) {
  let text = '';
  for (const b of octeti) text += String.fromCharCode(b);
  return btoa(text).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function conecteazaX(clientId) {
  // Politica X: autorizarea prin OAuth nu e, singura, acord. Omului i se spune dinainte, pe scurt,
  // ce va face aplicatia in numele contului si ca poate iesi oricand (X_PLAN.md, 10 oct 2026).
  if (!window.confirm(t('x', 'acordConectare'))) return;
  const brut = new Uint8Array(48);
  window.crypto.getRandomValues(brut);
  const verificator = base64Url(brut);
  const amprenta = base64Url(new Uint8Array(await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verificator))));
  // Adresa de intoarcere e fixa si trebuie sa se potriveasca EXACT cu cea din consola X.
  const redirectUri = encodeURIComponent(window.location.origin + '/auth/x/callback');
  // `media.write` pentru poze si filme, `offline.access` pentru tokenul de reinnoire (cel de
  // acces tine doar doua ore). Nimic de citit in afara de profilul propriu: la X citirile costa.
  const scope = encodeURIComponent('tweet.read tweet.write users.read media.write offline.access');
  const state = stareNoua(clientId, verificator);
  window.location.href = `https://x.com/i/oauth2/authorize?response_type=code&client_id=${encodeURIComponent(CLIENT_ID_X)}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}&code_challenge=${amprenta}&code_challenge_method=S256`;
}

// Deconectarea trece prin RPC, care verifica cine intreaba: `social_tokens` nu se
// poate atinge din browser din 15 septembrie 2026.
//
// Dupa ce randul a plecat, pleaca si poza contului din depozit (23 septembrie 2026).
// Randul tinea `poza_url`, dar fisierul ramanea acolo, fara nimic care sa mai trimita
// la el. Stergerea se cere functiei `sterge-poza-cont`, fiindca depozitul nu se atinge
// din browser. E best-effort DINADINS: daca pica, deconectarea ramane facuta. O poza
// ramasa in depozit nu e un motiv sa-i spui omului ca nu s-a deconectat.
export async function deconecteazaRetea(clientId, platform) {
  const raspuns = await supabase.rpc('deconecteaza_retea_client', {
    p_client_id: clientId,
    p_platform: platform,
  });
  if (!raspuns.error) await stergePozaContului(clientId, platform);
  return raspuns;
}

async function stergePozaContului(clientId, platform) {
  try {
    const { error } = await supabase.functions.invoke('sterge-poza-cont', {
      body: { client_id: clientId, platform },
    });
    if (error) console.log('Poza contului, nestearsa la deconectare:', platform, error.message);
  } catch (err) {
    console.log('Poza contului, nestearsa la deconectare:', platform, String(err));
  }
}
